import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, BookOpen, Brain, Activity,
  Apple, Eye, Ear, Smile, Users, Droplet, ShieldCheck, AlertTriangle
} from 'lucide-react';
import { ComprehensiveAssessmentResult } from '../../types/index';
// @ts-ignore
import yaml from 'js-yaml';

// --- Data Layer: Simulating a complete, processed knowledge base ---

const rawKnowledgeFiles = {
  // Universal Advice (Tier 1)
  'Advice-BrainHealth': `---
uid: ICOPE-C3-S3.1.3-Advice-BrainHealth
title: 优化大脑健康
verbatim_content: 'Undertake regular cognitive stimulation activities including reading, playing games, learning something new and playing music.'
related: ["ICOPE-C5-S5.2-Community-Care-Overview"]
---
为维持大脑健康，建议老年人定期进行认知刺激活动，例如阅读、玩游戏、学习新知识和演奏音乐等。`,
  'Advice-PhysicalActivity': `---
uid: ICOPE-C3-S3.1.3-Advice-PhysicalActivity
title: 进行规律的身体活动
verbatim_content: 'Start low, go slow and gradually increase physical activity. Options include walking, cycling, household tasks, gardening, dancing and sport. Avoid sedentary behaviour.'
related: ["ICOPE-C6-S6.1-LifestyleAdviceOverview"]
---
进行规律的身体活动对老年人非常有益。关键信息包括积少成多、多样化选择和避免久坐。`,
  // ... Add all other 10 universal advice files here for completeness
  
  // Intervention Advice (for failed domains)
  'Intervention-CommunityCareOverview': `---
uid: ICOPE-C3-S3.1.5-Community-Care-Overview
title: 社区层面干预总览
verbatim_content: 'When an older person has significant loss in intrinsic capacity, a community-based assessment should be triggered. This involves identifying the underlying causes and developing a personalized care plan.'
---
当内在能力出现显著下降时，应启动基于社区的评估。这包括确定根本原因并制定个性化的照护计划。`,
  'Intervention-Cognition': `---
uid: ICOPE-C5-S5.2-Community-Care-Overview
title: 认知能力社区干预
verbatim_content: 'For cognitive decline, interventions include social prescribing, cognitive stimulation therapies, and support for caregivers. Address potential underlying causes like hearing loss or depression.'
---
对于认知能力下降，干预措施包括社交处方、认知刺激疗法以及为照护者提供支持。同时需要处理潜在的根本原因，如听力损失或抑郁。`
  // ... Add other specific intervention files here
};

const parseMarkdown = (md: string) => {
  try {
    const parts = md.split('---');
    const frontMatter = yaml.load(parts[1]) as any;
    const content = parts[2].trim();
    return { ...frontMatter, content };
  } catch (e) { return null; }
};

const allKnowledge = Object.values(rawKnowledgeFiles).map(parseMarkdown).filter(Boolean);

const getKnowledge = (uid: string) => allKnowledge.find(k => k?.uid === uid);

const adviceData: Record<string, any> = allKnowledge
  .filter(k => k?.uid.includes('Advice'))
  .reduce((acc, item) => {
    if (item) {
      const key = item.uid.split('-').pop()?.toLowerCase();
      acc[key] = { ...item, icon: Brain };
    }
    return acc;
  }, {} as Record<string, any>);

const iconMap: Record<string, React.ElementType> = {
  brainhealth: Brain, physicalactivity: Activity, healthydiet: Apple, eyehealth: Eye,
  earhealth: Ear, mentalhealth: Smile, socialconnection: Users, oralhealth: Droplet,
  hydration: Droplet, diseasemanagement: ShieldCheck, vaccination: ShieldCheck, sleep: Droplet
};
Object.keys(adviceData).forEach(key => { if (iconMap[key]) adviceData[key].icon = iconMap[key]; });

const adviceCategories = Object.keys(adviceData);

// --- UI Components ---

const AdviceSection: React.FC = () => {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [deepDiveContent, setDeepDiveContent] = useState<any[] | null>(null);

  const handleSelectAdvice = (key: string) => {
    setSelectedKey(key);
    setDeepDiveContent(null);
  };

  const handleDeepDive = (key: string) => {
    const relatedUIDs = adviceData[key]?.related?.map((r: string) => r.replace(/\[|\]/g, '')) || [];
    const relatedContent = relatedUIDs.map((uid: string) => allKnowledge.find(k => k?.uid === uid)).filter(Boolean);
    setDeepDiveContent(relatedContent);
  };
  
  // AI Simulation for "What can you do?"
  const generateActionableSteps = (verbatim: string): string[] => {
    if (!verbatim) return ["具体的行动建议将由AI根据您的个人情况生成。"];
    return verbatim.split('.').map(s => s.trim()).filter(Boolean);
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm mb-8">
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">通用健康建议</h3>
        <p className="text-sm text-gray-600 mb-4">我们建议您关注以下健康生活方式。请点击您感兴趣的条目查看详细建议。</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
          {adviceCategories.map(key => {
            const item = adviceData[key];
            if (!item) return null;
            const Icon = item.icon;
            return (
              <button key={key} onClick={() => handleSelectAdvice(key)} className={`p-3 rounded-lg text-left transition-all duration-200 flex items-center space-x-3 ${selectedKey === key ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-100 hover:bg-gray-200'}`}>
                <Icon className={`w-5 h-5 ${selectedKey === key ? 'text-white' : 'text-blue-600'}`} />
                <span className="font-medium text-sm">{item.title}</span>
              </button>
            );
          })}
        </div>
        {selectedKey && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 animate-fade-in">
            <h4 className="text-lg font-bold text-blue-800 mb-3 flex items-center"><BookOpen className="w-5 h-5 mr-2" />{adviceData[selectedKey].title}</h4>
            <div className="prose prose-sm max-w-none text-gray-800">
              <p className="font-semibold">为什么它对您很重要？</p>
              <p>{adviceData[selectedKey].content}</p>
              <p className="font-semibold mt-4">您可以怎样做？</p>
              <ul className="list-disc pl-5 space-y-1">
                {generateActionableSteps(adviceData[selectedKey].verbatim_content).map((step, i) => <li key={i}>{step}</li>)}
              </ul>
            </div>
            <div className="mt-4 border-t border-blue-200 pt-4">
              {adviceData[selectedKey].related?.length > 0 && !deepDiveContent && (<button onClick={() => handleDeepDive(selectedKey)} className="text-sm font-semibold text-blue-600 hover:underline">了解更多 &raquo;</button>)}
              {deepDiveContent && (
                <div className="animate-fade-in">
                  <h5 className="font-bold text-gray-800 mb-2">深度阅读</h5>
                  <div className="space-y-2">
                    {deepDiveContent.map(item => (<div key={item.uid} className="bg-white p-3 rounded-md border"><p className="font-semibold text-sm text-gray-700">{item.title}</p></div>))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ResultsPage: React.FC<{ assessmentResult: ComprehensiveAssessmentResult | null }> = ({ assessmentResult }) => {
  const navigate = useNavigate();

  const domainNames = useMemo(() => ({
    cognition: '认知能力', mobility: '行动能力', vitality: '营养状况', 
    vision: '视力健康', hearing: '听力健康', psychologicalCapacity: '心理健康'
  }), []);

  if (!assessmentResult) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center"><h2 className="text-2xl font-bold mb-4">暂无评估结果</h2><button onClick={() => navigate('/assessment-choice')} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">开始评估</button></div>
      </div>
    );
  }

  const getOverallStatusInfo = () => {
    switch (assessmentResult.overallStatus) {
      case 'ALL_PASS': return { title: '恭喜！您的内在能力表现良好', color: 'text-green-600', bgColor: 'bg-green-50' };
      case 'SOME_FAIL': return { title: '您的内在能力需要一些关注', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
      case 'MULTIPLE_FAIL': return { title: '建议关注您的内在能力状况', color: 'text-red-600', bgColor: 'bg-red-50' };
    }
  };
  
  const getInterventionRecommendations = (domain: keyof typeof domainNames): any[] => {
    const overview = getKnowledge('ICOPE-C3-S3.1.5-Community-Care-Overview');
    let specific = null;
    if (domain === 'cognition') {
      specific = getKnowledge('ICOPE-C5-S5.2-Community-Care-Overview');
    }
    // Add other domain-specific intervention lookups here
    return [overview, specific].filter(Boolean);
  };

  const overallInfo = getOverallStatusInfo();
  const failedDomains = Object.entries(assessmentResult.intrinsicCapacity).filter(([, result]) => result.status === 'FAIL');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-10"><div className="max-w-4xl mx-auto px-4 py-4"><div className="flex items-center justify-between"><h1 className="text-xl font-bold text-gray-900">内在能力评估报告</h1></div></div></div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className={`${overallInfo.bgColor} border rounded-xl p-6 mb-8 text-center`}>
          <Heart className={`w-12 h-12 ${overallInfo.color} mx-auto mb-4`} />
          <h2 className={`text-2xl font-bold ${overallInfo.color}`}>{overallInfo.title}</h2>
        </div>

        {failedDomains.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center"><AlertTriangle className="w-6 h-6 text-red-500 mr-2" />需要优先关注的领域</h3>
            <div className="space-y-4">
              {failedDomains.map(([domain]) => {
                const recommendations = getInterventionRecommendations(domain as keyof typeof domainNames);
                return (
                  <div key={domain} className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-bold text-red-800 mb-2">{domainNames[domain as keyof typeof domainNames]}</h4>
                    <div className="text-sm text-red-700 space-y-2">
                      {recommendations.map((rec, i) => (
                        <div key={i}>
                          <p className="font-semibold">{rec.title}:</p>
                          <p>{rec.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <AdviceSection />

        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          <button onClick={() => navigate('/risk-assessment')} className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 flex items-center justify-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            深度风险评估
          </button>
          <button onClick={() => navigate('/consultant')} className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700">咨询专业顾问</button>
          <button onClick={() => navigate('/assessment-choice')} className="flex-1 bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700">重新评估</button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;